import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowRight, CheckCircle, AlertCircle, Loader, XCircle, ExternalLink } from 'lucide-react';

const InstitutionChecklistStep = () => {
  const navigate = useNavigate();

  // IGRS State
  const [igrsData, setIgrsData] = useState({ user_type: 'Citizen', mobile_email: '', password: '' });
  const [igrsStatus, setIgrsStatus] = useState('idle');
  const [igrsResult, setIgrsResult] = useState(null);

  // MaaBhoomi State
  const [maabhoomData, setMaabhoomData] = useState({ user_type: 'Citizen', mobile_email: '', password: '' });
  const [maabhoomStatus, setMaabhoomStatus] = useState('idle');
  const [maabhoomResult, setMaabhoomResult] = useState(null);

  // CCLA State
  const [cclaData, setCclaData] = useState({ username: '', password: '' });
  const [cclaStatus, setCclaStatus] = useState('idle');
  const [cclaResult, setCclaResult] = useState(null);

  // ROC State
  const [rocData, setRocData] = useState({ email: '', password: '' });
  const [rocStatus, setRocStatus] = useState('idle');
  const [rocResult, setRocResult] = useState(null);

  const runIgrsCheck = async () => {
    if (!igrsData.mobile_email || !igrsData.password) {
      alert('Please fill in all IGRS credentials');
      return;
    }

    setIgrsStatus('loading');
    setIgrsResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/institution-checklist/run`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          igrs_user_type: igrsData.user_type,
          igrs_mobile_email: igrsData.mobile_email,
          igrs_password: igrsData.password,
          owner: 'Portal Test',
          district: 'Hyderabad',
          village: 'Test Village',
          survey_no: '123'
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('IGRS API Response:', data);
      
      const igrsPortalResult = data.results?.find(r => r.source === 'IGRS');
      
      if (igrsPortalResult?.status === 'success') {
        setIgrsStatus('success');
        setIgrsResult(igrsPortalResult);
      } else {
        setIgrsStatus('error');
        setIgrsResult(igrsPortalResult || { error: 'Verification failed' });
      }
    } catch (err) {
      console.error('IGRS Error:', err);
      setIgrsStatus('error');
      setIgrsResult({ error: err.message || 'Network error occurred' });
    }
  };

  const runMaabhoomCheck = async () => {
    if (!maabhoomData.mobile_email || !maabhoomData.password) {
      alert('Please fill in all MaaBhoomi credentials');
      return;
    }

    setMaabhoomStatus('loading');
    setMaabhoomResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/institution-checklist/run`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          maabhoomi_user_type: maabhoomData.user_type,
          maabhoomi_mobile_email: maabhoomData.mobile_email,
          maabhoomi_password: maabhoomData.password,
          owner: 'Portal Test',
          district: 'Hyderabad',
          village: 'Test Village',
          survey_no: '123'
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('MaaBhoomi API Response:', data);
      
      const maabhoomPortalResult = data.results?.find(r => r.source === 'MaaBhoomi');
      
      if (maabhoomPortalResult?.status === 'success') {
        setMaabhoomStatus('success');
        setMaabhoomResult(maabhoomPortalResult);
      } else {
        setMaabhoomStatus('error');
        setMaabhoomResult(maabhoomPortalResult || { error: 'Verification failed' });
      }
    } catch (err) {
      console.error('MaaBhoomi Error:', err);
      setMaabhoomStatus('error');
      setMaabhoomResult({ error: err.message || 'Network error occurred' });
    }
  };

  const runCclaCheck = async () => {
    if (!cclaData.username || !cclaData.password) {
      alert('Please fill in all CCLA credentials');
      return;
    }

    setCclaStatus('loading');
    setCclaResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/institution-checklist/run`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          ccla_username: cclaData.username,
          ccla_password: cclaData.password,
          owner: 'Portal Test',
          district: 'Hyderabad'
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('CCLA API Response:', data);
      
      const cclaPortalResult = data.results?.find(r => r.source === 'CCLA');
      
      if (cclaPortalResult?.status === 'success') {
        setCclaStatus('success');
        setCclaResult(cclaPortalResult);
      } else {
        setCclaStatus('error');
        setCclaResult(cclaPortalResult || { error: 'Verification failed' });
      }
    } catch (err) {
      console.error('CCLA Error:', err);
      setCclaStatus('error');
      setCclaResult({ error: err.message || 'Network error occurred' });
    }
  };

  const runRocCheck = async () => {
    if (!rocData.email || !rocData.password) {
      alert('Please fill in all ROC credentials');
      return;
    }

    setRocStatus('loading');
    setRocResult(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/institution-checklist/run`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          roc_email: rocData.email,
          roc_password: rocData.password,
          owner: 'Test Company Pvt Ltd',
          company_cin: 'U12345TG2018PLC123456'
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('ROC API Response:', data);
      
      const rocPortalResult = data.results?.find(r => r.source === 'ROC');
      
      if (rocPortalResult?.status === 'success') {
        setRocStatus('success');
        setRocResult(rocPortalResult);
      } else {
        setRocStatus('error');
        setRocResult(rocPortalResult || { error: 'Verification failed' });
      }
    } catch (err) {
      console.error('ROC Error:', err);
      setRocStatus('error');
      setRocResult({ error: err.message || 'Network error occurred' });
    }
  };

  const handleProceedToNextStep = () => {
    navigate('/extract');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'loading': return <Loader className="w-5 h-5 text-blue-500 animate-spin" />;
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error': return <XCircle className="w-5 h-5 text-red-500" />;
      default: return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'loading': return <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">Verifying...</span>;
      case 'success': return <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">Verified ✓</span>;
      case 'error': return <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">Failed ✗</span>;
      default: return <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-semibold rounded-full">Not Started</span>;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Portal Verification</h1>
                <p className="text-sm text-gray-500">Test your government portal credentials independently</p>
              </div>
            </div>
            <button
              onClick={handleProceedToNextStep}
              className="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white hover:bg-green-700 rounded-lg transition-colors font-semibold"
            >
              <span>Proceed to Next Step</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <div className="flex items-start">
            <Shield className="w-6 h-6 text-blue-600 mr-3 mt-1" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Independent Portal Verification</h3>
              <p className="text-blue-800 text-sm mb-2">
                Each government portal can be verified independently. You can test whichever portals you have credentials for and skip the rest.
              </p>
              <p className="text-blue-700 text-xs font-medium">
                ✓ No interdependence between portals  •  ✓ Skip any portal  •  ✓ Proceed anytime
              </p>
            </div>
          </div>
        </div>

        {/* Portal Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* IGRS Card */}
          <div className="bg-white rounded-xl shadow-md border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    IGRS
                  </h3>
                  <p className="text-blue-100 text-xs mt-1">Encumbrance Certificate</p>
                </div>
                {getStatusBadge(igrsStatus)}
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">User Type</label>
                  <select
                    value={igrsData.user_type}
                    onChange={(e) => setIgrsData({ ...igrsData, user_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={igrsStatus === 'loading'}
                  >
                    <option value="Citizen">Citizen</option>
                    <option value="Advocate">Advocate</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mobile / Email</label>
                  <input
                    type="text"
                    value={igrsData.mobile_email}
                    onChange={(e) => setIgrsData({ ...igrsData, mobile_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your mobile or email"
                    disabled={igrsStatus === 'loading'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    value={igrsData.password}
                    onChange={(e) => setIgrsData({ ...igrsData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter your password"
                    disabled={igrsStatus === 'loading'}
                  />
                </div>

                <button
                  onClick={runIgrsCheck}
                  disabled={igrsStatus === 'loading'}
                  className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
                >
                  {getStatusIcon(igrsStatus)}
                  <span>{igrsStatus === 'loading' ? 'Verifying...' : 'Run Verification'}</span>
                </button>

                {igrsResult && (
                  <div className={`mt-4 p-3 rounded-lg text-sm ${igrsStatus === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {igrsStatus === 'success' ? '✓ Verification successful!' : `✗ ${igrsResult.error || 'Verification failed'}`}
                  </div>
                )}

                {/* Portal Links */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-3 flex items-center">
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Access Portal Directly:
                  </p>
                  <div className="space-y-2">
                    <a
                      href="https://registration.telangana.gov.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors group"
                    >
                      <span className="text-xs text-blue-700 font-medium">IGRS Portal - Telangana</span>
                      <ExternalLink className="w-3 h-3 text-blue-600 group-hover:translate-x-1 transition-transform" />
                    </a>
                    <a
                      href="https://registration.telangana.gov.in/EncumbranceSearch.htm"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors group"
                    >
                      <span className="text-xs text-blue-700 font-medium">Encumbrance Certificate Search</span>
                      <ExternalLink className="w-3 h-3 text-blue-600 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* MaaBhoomi Card */}
          <div className="bg-white rounded-xl shadow-md border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    MaaBhoomi
                  </h3>
                  <p className="text-green-100 text-xs mt-1">Land Records</p>
                </div>
                {getStatusBadge(maabhoomStatus)}
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">User Type</label>
                  <select
                    value={maabhoomData.user_type}
                    onChange={(e) => setMaabhoomData({ ...maabhoomData, user_type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    disabled={maabhoomStatus === 'loading'}
                  >
                    <option value="Citizen">Citizen</option>
                    <option value="Official">Official</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Mobile / Email</label>
                  <input
                    type="text"
                    value={maabhoomData.mobile_email}
                    onChange={(e) => setMaabhoomData({ ...maabhoomData, mobile_email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter your mobile or email"
                    disabled={maabhoomStatus === 'loading'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    value={maabhoomData.password}
                    onChange={(e) => setMaabhoomData({ ...maabhoomData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Enter your password"
                    disabled={maabhoomStatus === 'loading'}
                  />
                </div>

                <button
                  onClick={runMaabhoomCheck}
                  disabled={maabhoomStatus === 'loading'}
                  className="w-full flex items-center justify-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:bg-green-400 disabled:cursor-not-allowed"
                >
                  {getStatusIcon(maabhoomStatus)}
                  <span>{maabhoomStatus === 'loading' ? 'Verifying...' : 'Run Verification'}</span>
                </button>

                {maabhoomResult && (
                  <div className={`mt-4 p-3 rounded-lg text-sm ${maabhoomStatus === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {maabhoomStatus === 'success' ? '✓ Verification successful!' : `✗ ${maabhoomResult.error || 'Verification failed'}`}
                  </div>
                )}

                {/* Portal Links */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-3 flex items-center">
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Access Portal Directly:
                  </p>
                  <div className="space-y-2">
                    <a
                      href="https://bhubharati.telangana.gov.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-3 py-2 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors group"
                    >
                      <span className="text-xs text-green-700 font-medium">Bhubharati Portal - Telangana</span>
                      <ExternalLink className="w-3 h-3 text-green-600 group-hover:translate-x-1 transition-transform" />
                    </a>
                    <a
                      href="https://bhubharati.telangana.gov.in/knowLandStatus"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-3 py-2 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors group"
                    >
                      <span className="text-xs text-green-700 font-medium">Land Records Search</span>
                      <ExternalLink className="w-3 h-3 text-green-600 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* CCLA Card */}
          <div className="bg-white rounded-xl shadow-md border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    CCLA
                  </h3>
                  <p className="text-purple-100 text-xs mt-1">Mutation Records</p>
                </div>
                {getStatusBadge(cclaStatus)}
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
                  <input
                    type="text"
                    value={cclaData.username}
                    onChange={(e) => setCclaData({ ...cclaData, username: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter your username"
                    disabled={cclaStatus === 'loading'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    value={cclaData.password}
                    onChange={(e) => setCclaData({ ...cclaData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter your password"
                    disabled={cclaStatus === 'loading'}
                  />
                </div>

                <button
                  onClick={runCclaCheck}
                  disabled={cclaStatus === 'loading'}
                  className="w-full flex items-center justify-center space-x-2 bg-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors disabled:bg-purple-400 disabled:cursor-not-allowed"
                >
                  {getStatusIcon(cclaStatus)}
                  <span>{cclaStatus === 'loading' ? 'Verifying...' : 'Run Verification'}</span>
                </button>

                {cclaResult && (
                  <div className={`mt-4 p-3 rounded-lg text-sm ${cclaStatus === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {cclaStatus === 'success' ? '✓ Verification successful!' : `✗ ${cclaResult.error || 'Verification failed'}`}
                  </div>
                )}

                {/* Portal Links */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-3 flex items-center">
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Access Portal Directly:
                  </p>
                  <div className="space-y-2">
                    <a
                      href="https://ccla.telangana.gov.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors group"
                    >
                      <span className="text-xs text-purple-700 font-medium">CCLA Portal - Telangana</span>
                      <ExternalLink className="w-3 h-3 text-purple-600 group-hover:translate-x-1 transition-transform" />
                    </a>
                    <a
                      href="https://ccla.telangana.gov.in/integratedLandRegistry.do"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors group"
                    >
                      <span className="text-xs text-purple-700 font-medium">Integrated Land Registry</span>
                      <ExternalLink className="w-3 h-3 text-purple-600 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ROC Card */}
          <div className="bg-white rounded-xl shadow-md border-2 border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center">
                    <Shield className="w-5 h-5 mr-2" />
                    ROC
                  </h3>
                  <p className="text-orange-100 text-xs mt-1">Company Charges</p>
                </div>
                {getStatusBadge(rocStatus)}
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={rocData.email}
                    onChange={(e) => setRocData({ ...rocData, email: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter your email"
                    disabled={rocStatus === 'loading'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    value={rocData.password}
                    onChange={(e) => setRocData({ ...rocData, password: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter your password"
                    disabled={rocStatus === 'loading'}
                  />
                </div>

                <button
                  onClick={runRocCheck}
                  disabled={rocStatus === 'loading'}
                  className="w-full flex items-center justify-center space-x-2 bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:bg-orange-400 disabled:cursor-not-allowed"
                >
                  {getStatusIcon(rocStatus)}
                  <span>{rocStatus === 'loading' ? 'Verifying...' : 'Run Verification'}</span>
                </button>

                {rocResult && (
                  <div className={`mt-4 p-3 rounded-lg text-sm ${rocStatus === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {rocStatus === 'success' ? '✓ Verification successful!' : `✗ ${rocResult.error || 'Verification failed'}`}
                  </div>
                )}

                {/* Portal Links */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-3 flex items-center">
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Access Portal Directly:
                  </p>
                  <div className="space-y-2">
                    <a
                      href="https://www.mca.gov.in"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors group"
                    >
                      <span className="text-xs text-orange-700 font-medium">MCA Portal - India</span>
                      <ExternalLink className="w-3 h-3 text-orange-600 group-hover:translate-x-1 transition-transform" />
                    </a>
                    <a
                      href="https://www.mca.gov.in/mcafoportal/companyLLPMasterData.do"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors group"
                    >
                      <span className="text-xs text-orange-700 font-medium">Company Master Data Search</span>
                      <ExternalLink className="w-3 h-3 text-orange-600 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Proceed Button at Bottom */}
        <div className="mt-10 text-center">
          <button
            onClick={handleProceedToNextStep}
            className="inline-flex items-center space-x-3 bg-gradient-to-r from-green-500 to-green-600 text-white px-10 py-4 rounded-xl font-bold text-lg hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl"
          >
            <span>Proceed to Next Step</span>
            <ArrowRight className="w-6 h-6" />
          </button>
          <p className="text-gray-500 text-sm mt-3">You can verify portals anytime. No need to complete all verifications.</p>
        </div>
      </main>
    </div>
  );
};

export default InstitutionChecklistStep;
